import React, { useEffect, useState } from 'react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext'; 
import { 
  CheckSquare, 
  Target, 
  Users, 
  Building2,
  ArrowRight,
  Calendar,
  Clock,
  Filter,
  AlertCircle,
  MessageSquare,
  User
} from 'lucide-react';

const WeeklyTaskSelector = ({ 
  selectedTasks, 
  setSelectedTasks, 
  selectedTaskType, 
  setSelectedTaskType, 
  onNext,
  weekDates,
  dayNames,
  draggedTaskIds = []
}) => {
  const { tasks = [], projects = [], users = [], departments = [], teams = [] } = useData();
  const { currentUser } = useAuth();
  const [filterAssignedToMe, setFilterAssignedToMe] = useState('all'); // all או assigned

  // פונקציה לקבלת כל המשימות שכבר תוכננו
  const getAlreadyScheduledTaskIds = () => {
    const scheduledIds = new Set();
    
    try {
      // בדיקת תכנונים יומיים
      const dailyPlans = JSON.parse(localStorage.getItem('dailyPlans') || '[]');
      dailyPlans.forEach(plan => {
        if (plan.schedule) {
          Object.values(plan.schedule).forEach(slot => {
            if (slot?.task?.id) {
              scheduledIds.add(slot.task.id);
            }
          });
        }
      });

      // בדיקת תכנונים שבועיים
      const weeklyPlans = JSON.parse(localStorage.getItem('weeklyPlans') || '[]');
      weeklyPlans.forEach(plan => {
        if (plan.summaryData?.schedule) {
          Object.values(plan.summaryData.schedule).forEach(daySchedule => {
            Object.values(daySchedule || {}).forEach(task => {
              if (task?.id) {
                scheduledIds.add(task.id);
              } else if (task?.task?.id) {
                scheduledIds.add(task.task.id);
              }
            });
          });
        }
      });
    } catch (error) {
      console.error('Error loading scheduled tasks:', error);
    }

    // הוספת משימות שנגררו בסשן הנוכחי
    draggedTaskIds.forEach(id => scheduledIds.add(id));

    // בדיקה נוספת - משימות מה-state הנוכחי של הלוח השבועי
try {
  // בדוק אם יש weeklyScheduleState בפרופס
  if (window.weeklyScheduleState) {
    Object.values(window.weeklyScheduleState).forEach(daySchedule => {
      if (daySchedule) {
        Object.values(daySchedule).forEach(task => {
          if (task?.id) {
            scheduledIds.add(task.id);
          }
        });
      }
    });
  }
} catch (error) {
  console.log('No global weekly state');
}

    return scheduledIds;
  };

  // פילטר משימות לפי הרשאות משתמש
  const filterTasksByPermissions = (tasksList) => {
    const scheduledIds = getAlreadyScheduledTaskIds();
    
    // סינון משימות שכבר תוכננו
    let filteredTasks = tasksList.filter(task => !scheduledIds.has(task.id));
    
    // סינון משימות שגרה
    filteredTasks = filteredTasks.filter(task => {
      if (task.isDailyRoutine || task.routineId || task.createdFrom === 'daily_routine') {
        return false;
      }
      // בדיקה אם השם מכיל תאריך
      if (task.title && task.title.includes(' - ') && task.title.match(/\d{1,2}\/\d{1,2}\/\d{4}/)) {
        return false;
      }
      return true;
    });

    // סינון לפי הרשאות המשתמש
    const userRole = currentUser?.role;
    const userId = currentUser?.id;
    const userTeamId = currentUser?.teamId;
    const userDeptId = currentUser?.departmentId;

    switch (userRole) {
      case 'employee':
        // עובד רגיל - רק משימות שלו
        filteredTasks = filteredTasks.filter(task => 
          task.assigneeId === userId ||
          task.creatorId === userId ||
          (Array.isArray(task.assigneeIds) && task.assigneeIds.includes(userId))
        );
        break;

      case 'team_manager':
        // מנהל צוות - רק משימות של הצוות שלו
        if (userTeamId) {
          const teamMemberIds = users
            .filter(u => u.teamId === userTeamId)
            .map(u => u.id);
          
          filteredTasks = filteredTasks.filter(task =>
            teamMemberIds.includes(task.assigneeId) ||
            teamMemberIds.includes(task.creatorId) ||
            (Array.isArray(task.assigneeIds) && 
             task.assigneeIds.some(id => teamMemberIds.includes(id))) ||
            task.teamId === userTeamId
          );
        } else {
          filteredTasks = filteredTasks.filter(task => 
            task.assigneeId === userId ||
            task.creatorId === userId
          );
        }
        break;

      case 'dept_manager':
        // מנהל מחלקה - משימות של המחלקה שלו
        if (userDeptId) {
          const deptMemberIds = users
            .filter(u => u.departmentId === userDeptId)
            .map(u => u.id);
          
          filteredTasks = filteredTasks.filter(task =>
            deptMemberIds.includes(task.assigneeId) ||
            deptMemberIds.includes(task.creatorId) ||
            (Array.isArray(task.assigneeIds) && 
             task.assigneeIds.some(id => deptMemberIds.includes(id))) ||
            task.departmentId === userDeptId
          );
        }
        break;

      case 'org_admin':
      case 'ceo':
      case 'deputy_ceo':
        // הנהלה - רואים הכל, אין צורך בסינון
        break;

      default:
        // ברירת מחדל - רק משימות של המשתמש
        filteredTasks = filteredTasks.filter(task => 
          task.assigneeId === userId ||
          task.creatorId === userId
        );
    }

    return filteredTasks;
  };

  // Get filtered tasks based on type
  const getFilteredTasks = () => {
    let filteredTasks = filterTasksByPermissions(tasks);

  if (selectedTaskType === 'managerial') {
  filteredTasks = filteredTasks.filter(task => 
    task.scope !== 'personal' &&
    task.isPersonal !== true
  );
} else {
  filteredTasks = filteredTasks.filter(task => 
    (task.scope === 'personal' || task.isPersonal === true) &&
    (task.assigneeId === currentUser?.id || task.creatorId === currentUser?.id)
  );
}

    // פילטר למשימות שהוקצו אליי בלבד (לא כולל משימות שיצרתי)
    if (filterAssignedToMe === 'assigned') {
      const userId = currentUser?.id;
      filteredTasks = filteredTasks.filter(task => 
        task.assigneeId === userId ||
        (Array.isArray(task.assigneeIds) && task.assigneeIds.includes(userId))
      );
    }

    return filteredTasks;
  };

  // עדכון משימות נבחרות כשמשימות נגררות או תוכננו
  useEffect(() => {
    const scheduledIds = getAlreadyScheduledTaskIds();
    setSelectedTasks(prev => 
      prev.filter(task => !scheduledIds.has(task.id))
    );
  }, [draggedTaskIds, setSelectedTasks]);

  const handleTaskToggle = (task) => {
    setSelectedTasks(prev => 
      prev.some(t => t.id === task.id) 
        ? prev.filter(t => t.id !== task.id)
        : [...prev, task]
    );
  };

  const getTaskProject = (task) => {
    return projects.find(p => p.id === task.projectId);
  };

  const getTaskAssignee = (task) => {
    return users.find(u => u.id === task.assigneeId);
  };

  const filteredTasks = getFilteredTasks();
  const scheduledCount = getAlreadyScheduledTaskIds().size;

  const getPermissionMessage = () => {
    const role = currentUser?.role;
    const teamName = currentUser?.teamName;
    const deptName = currentUser?.departmentName;

    switch (role) {
      case 'team_manager':
        return `מוצגות משימות של צוות: ${teamName || 'לא מוגדר'}`;
      case 'dept_manager':
        return `מוצגות משימות של מחלקת: ${deptName || 'לא מוגדר'}`;
      case 'employee':
        return 'מוצגות המשימות שלך בלבד';
      case 'org_admin':
      case 'ceo':
      case 'deputy_ceo':
        return 'מוצגות כל המשימות בארגון';
      default:
        return 'מוצגות משימות לפי הרשאות';
    }
  };

  const handleSelectAll = () => {
    setSelectedTasks([...filteredTasks]);
  };

  const handleDeselectAll = () => {
    setSelectedTasks([]);
  };

  const handleNext = () => {
    // עבור ישירות לתכנון זמנים ללא שלב הערות
    onNext();
  };

  return (
    <div className="space-y-8">
      {/* Week Overview */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
          <Calendar className="w-6 h-6 text-blue-600" />
          תכנון השבוע הקרוב
        </h2>
        
        {/* הודעת הרשאות */}
        <div className="mb-4 p-3 bg-blue-100 rounded-xl flex items-center gap-2">
          <Filter className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-800">
            {getPermissionMessage()}
          </span>
        </div>

        {/* הודעה על משימות שתוכננו */}
        {scheduledCount > 0 && (
          <div className="mb-4 p-3 bg-yellow-100 rounded-xl flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">
              {scheduledCount} משימות כבר תוכננו ולא יופיעו ברשימה
            </span>
          </div>
        )}
        
        <div className="grid grid-cols-7 gap-4">
          {weekDates.map((date, index) => (
            <div key={index} className="text-center">
              <div className="text-sm font-medium text-gray-600">{dayNames[index]}</div>
              <div className="text-lg font-bold text-gray-900">{date.getDate()}</div>
              <div className="text-xs text-gray-500">{date.toLocaleDateString('he-IL', { month: 'short' })}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Task Type Selector */}
      <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl text-white">
            <Target className="w-5 h-5" />
          </div>
          בחירת סוג משימות
        </h3>
        
        <div className="flex space-x-2 bg-gray-100/80 p-2 rounded-2xl backdrop-blur-sm">
          <button
            onClick={() => setSelectedTaskType('managerial')}
            className={`flex-1 py-4 px-6 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-3 ${
              selectedTaskType === 'managerial'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg scale-105'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
          >
            <Building2 className="w-5 h-5" />
            משימות ניהוליות
          </button>
          <button
            onClick={() => setSelectedTaskType('personal')}
            className={`flex-1 py-4 px-6 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-3 ${
              selectedTaskType === 'personal'
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg scale-105'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
          >
            <User className="w-5 h-5" />
            משימות אישיות
          </button>
        </div>
      </div>

      {/* Task Selection */}
      <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl text-white">
            <CheckSquare className="w-5 h-5" />
          </div>
          בחר משימות לתכנון השבוע
        </h3>

        {/* Assigned To Me Filter - NEW! */}
        {(currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo' || currentUser?.role === 'org_admin') && (
          <div className="mb-6">
            <select
              value={filterAssignedToMe}
              onChange={(e) => setFilterAssignedToMe(e.target.value)}
              className="w-full px-4 py-3 border-2 border-purple-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-purple-50 to-pink-50 font-semibold shadow-sm hover:shadow-md"
            >
              <option value="all">🌐 כל המשימות</option>
              <option value="assigned">👤 משימות שהוקצו אליי</option>
            </select>
          </div>
        )}

        {/* Task count info */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <span className="text-sm text-gray-600">
              {filteredTasks.length} משימות זמינות
            </span>
            {selectedTasks.length > 0 && (
              <span className="text-sm font-medium text-blue-600 mr-4">
                {selectedTasks.length} משימות נבחרו
              </span>
            )}
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleSelectAll}
              disabled={filteredTasks.length === 0}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all text-sm font-medium"
            >
              ✓ בחר הכל
            </button>
            <button
              onClick={handleDeselectAll}
              disabled={selectedTasks.length === 0}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all text-sm font-medium"
            >
              ✕ בטל הכל
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-h-96 overflow-y-auto">
          {filteredTasks.map(task => {
            const project = getTaskProject(task);
            const assignee = getTaskAssignee(task);
            const isSelected = selectedTasks.some(t => t.id === task.id);
            
            return (
              <div
                key={task.id}
                className={`p-6 rounded-3xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  isSelected 
                    ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg' 
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/30'
                }`}
                onClick={() => handleTaskToggle(task)}
              >
                <div className="flex items-start justify-between mb-4">
                  <h4 className="font-bold text-gray-900 text-lg leading-tight">{task.title}</h4>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                    isSelected ? 'bg-green-500 border-green-500' : 'border-gray-300 hover:border-green-400'
                  }`}>
                    {isSelected && <span className="text-white text-sm">✓</span>}
                  </div>
                </div>
                
                {task.description && (
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-2">
                    {task.description}
                  </p>
                )}
                
                <div className="space-y-3">
                  {project && (
                    <div className="flex items-center gap-3 p-2 bg-blue-50 rounded-xl">
                      <Building2 className="w-5 h-5 text-blue-500" />
                      <span className="text-sm font-medium text-blue-700">{project.name}</span>
                    </div>
                  )}
                  
                  {assignee && (
                    <div className="flex items-center gap-3 p-2 bg-purple-50 rounded-xl">
                      <Users className="w-5 h-5 text-purple-500" />
                      <span className="text-sm font-medium text-purple-700">{assignee.name}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3">
                    <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                      task.priority === 'high' ? 'bg-red-100 text-red-800' :
                      task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {task.priority === 'high' ? 'גבוהה' : 
                       task.priority === 'medium' ? 'בינונית' : 'נמוכה'}
                    </span>
                    {task.dueDate && (
                      <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                        📅 {new Date(task.dueDate).toLocaleDateString('he-IL')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          
          {filteredTasks.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              <CheckSquare className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-lg">אין משימות זמינות</p>
              <p className="text-sm mt-2">
                {scheduledCount > 0 
                  ? 'כל המשימות כבר תוכננו' 
                  : 'אין משימות התואמות את ההרשאות שלך'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Selected Tasks Summary */}
      {selectedTasks.length > 0 && (
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl text-white">
              <Target className="w-5 h-5" />
            </div>
            משימות נבחרות ({selectedTasks.length})
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {selectedTasks.map(task => {
              const project = getTaskProject(task);
              return (
                <div
                  key={task.id}
                  className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900 text-sm">{task.title}</h4>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTaskToggle(task);
                      }}
                      className="w-4 h-4 bg-red-500 hover:bg-red-600 rounded-full text-white text-xs flex items-center justify-center transition-colors"
                    >
                      ×
                    </button>
                  </div>
                  
                  {project && (
                    <div className="text-xs text-blue-600 mb-2">📁 {project.name}</div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      task.priority === 'high' ? 'bg-red-100 text-red-800' :
                      task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {task.priority === 'high' ? 'גבוהה' : 
                       task.priority === 'medium' ? 'בינונית' : 'נמוכה'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Next Button */}
      <div className="flex justify-end">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          disabled={selectedTasks.length === 0}
          className={`px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 flex items-center gap-3 ${
            selectedTasks.length > 0
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:scale-105'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          <span>הוסף הערות ומטרות</span>
          <ArrowRight className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

export default WeeklyTaskSelector;